import { useQuery } from "@tanstack/react-query";
import { apiClient } from "src/dm/utils/axios";
import { APIS } from "src/dm/utils/constants";
import {
  DealErrorEntry,
  DealFeeEntry,
  DealHistoryEntry,
  DealSignatureEntry,
  ListEnvelope,
} from "src/dm/interfaces/DealActivity";

const dealUrl = (dealId: number, suffix: string) =>
  `${APIS.Deals}/${dealId}/${suffix}`;

const makeListHook = <T>(suffix: string, keyPrefix: string) =>
  (dealId: number | undefined) =>
    useQuery({
      queryKey: [keyPrefix, dealId],
      enabled: dealId !== undefined && dealId > 0,
      queryFn: async (): Promise<T[]> => {
        const response = await apiClient.get<ListEnvelope<T>>(
          dealUrl(dealId as number, suffix)
        );
        return response.data.items;
      },
    });

export const useDealHistory = makeListHook<DealHistoryEntry>("history", "deal-history");
export const useDealSignatures = makeListHook<DealSignatureEntry>("signatures", "deal-signatures");
export const useDealErrors = makeListHook<DealErrorEntry>("errors", "deal-errors");
export const useDealFees = makeListHook<DealFeeEntry>("fees", "deal-fees");

/**
 * Grouped + lookup-resolved drill-down for one DEAL_CHANGE_HIST row.
 * Returns the DEAL_CHANGE_HIST_DETAIL rows for the supplied DHST_ID
 * grouped by TBL_NME, with friendly descriptions filled in for the
 * coded columns (AD_SZ_CD / AD_DIST / AD_LOCN_ID / AD_PLAN_CD / PROM_CD).
 * Backs the SPA's ViewDealHistory drill-down — see audit
 * 14-parity-matrix.md §5 item 18.
 */
export interface HistoryDetailResolvedRow {
  historyDetailId: number;
  columnName: string;
  operationType: string;
  oldValue: string;
  newValue: string;
  oldValueResolved: string | null;
  newValueResolved: string | null;
}
export interface HistoryDetailGroup {
  tableName: string;
  rows: HistoryDetailResolvedRow[];
}
export interface HistoryDetailsGroupedResponse {
  groups: HistoryDetailGroup[];
  total: number;
}

export const useGroupedHistory = (
  dealId: number | undefined,
  historyId: number | null | undefined
) =>
  useQuery({
    queryKey: ["deal-history-grouped", dealId, historyId],
    enabled:
      dealId !== undefined && dealId > 0 &&
      historyId !== undefined && historyId !== null && historyId > 0,
    queryFn: async (): Promise<HistoryDetailGroup[]> => {
      const response = await apiClient.get<HistoryDetailsGroupedResponse>(
        `${APIS.Deals}/${dealId}/history/${historyId}/details/grouped`
      );
      return response.data.groups;
    },
  });

/**
 * Chronological timeline that interleaves DEAL_NOTE entries with
 * DEAL_CHANGE_HIST rows — the SPA port of legacy ViewDealHistory's
 * "show notes alongside status changes" presentation, which legacy
 * implements via DealNote.GetByDateRange between two history timestamps.
 */
export interface HistoryTimelineItem {
  kind: "note" | "change";
  when: string | null;
  actor: string | null;
  label: string | null;
  text: string | null;
  historyId: number | null;
  noteId: number | null;
  isInternalNote: boolean | null;
}
export interface HistoryTimelineResponse {
  items: HistoryTimelineItem[];
  total: number;
}

export const useHistoryWithNotes = (
  dealId: number | undefined,
  range?: { fromDate?: string; toDate?: string }
) =>
  useQuery({
    queryKey: ["deal-history-with-notes", dealId, range?.fromDate, range?.toDate],
    enabled: dealId !== undefined && dealId > 0,
    queryFn: async (): Promise<HistoryTimelineItem[]> => {
      const response = await apiClient.get<HistoryTimelineResponse>(
        `${APIS.Deals}/${dealId}/history-with-notes`,
        { params: range }
      );
      return response.data.items;
    },
  });
